// Lazy AI: say a back-office change instead of typing it. Tap the microphone, say "Add Budweiser 12-pack, cost $10.99, sell it
// for $15.99", and Lazy shows a card with exactly what it will save. Nothing is saved until you tap Confirm.
// Browsers only listen while the microphone button is on, so "hey Lazy" is said after the tap (it is fine to say it or not).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, usd } from '../lib/api';
import { ErrorNote, Icon } from './ui';
import { useVoice } from './Shailesh';

interface Option { id: string; kind: 'store' | 'catalog' | 'new'; label: string; detail?: string; exact?: boolean }
interface Fields {
  brand?: string; name?: string; size?: string; pack?: number; category?: string; costCents?: number | null; priceCents?: number | null;
  code?: string | null; openingStock?: number | null; qty?: number | null; location?: string | null;
}
interface Plan { action: string; title: string; question: string; options: Option[]; choice: string | null; fields: Fields; facts: string[]; warnings: string[]; blocked: string | null }
interface Invoice { item: string | null; invoiceId: string; invoiceNo: string | null; date: string | null; wholesaler: string | null; line: string; cases: number; bottles: number; caseCents: number | null; link: string }
interface Cmd { id?: string; status: 'pending' | 'done' | 'cancelled' | 'failed' | 'unclear'; said?: string; action?: string; plan?: Plan; message?: string;
  result?: { message?: string; link?: string; invoices?: Invoice[] } | null; createdAt?: string; who?: string }

const EXAMPLES = [
  'Add Budweiser 12-pack, cost $10.99, sell it for $15.99',
  "Change the price of Tito's 1.75 to $32.99",
  'Show me the invoice where I last bought Budweiser',
  "We have 14 of Tito's 1.75",
  'Move Jameson 750 to aisle 3',
];
const CATEGORIES = ['spirits', 'wine', 'beer', 'rtd', 'cider', 'non_alcoholic', 'accessory'];

const toCents = (s: string) => { const n = Number(s.replace(/[$,\s]/g, '')); return s.trim() === '' || !Number.isFinite(n) ? null : Math.round(n * 100); };
const dollars = (c: number | null | undefined) => (c == null ? '' : (c / 100).toFixed(2));

/** Says the card's question out loud after a spoken command, so you can keep your eyes on the shelf. */
function speak(text: string) {
  try { if ('speechSynthesis' in window) { window.speechSynthesis.cancel(); window.speechSynthesis.speak(new SpeechSynthesisUtterance(text)); } } catch { /* no voice */ }
}

/** A money box that keeps what you type until you leave it. */
function MoneyInput({ label, cents, onChange }: { label: string; cents: number | null | undefined; onChange: (c: number | null) => void }) {
  const [v, setV] = useState(dollars(cents));
  useEffect(() => setV(dollars(cents)), [cents]);
  return <label>{label}<input inputMode="decimal" value={v} placeholder="0.00" onChange={(e) => setV(e.target.value)} onBlur={() => { const c = toCents(v); if (c !== (cents ?? null)) onChange(c); }} /></label>;
}
function TextInput({ label, value, onChange, numeric, placeholder }: { label: string; value: string | number | null | undefined; onChange: (v: string) => void; numeric?: boolean; placeholder?: string }) {
  const [v, setV] = useState(value == null ? '' : String(value));
  useEffect(() => setV(value == null ? '' : String(value)), [value]);
  return <label>{label}<input inputMode={numeric ? 'numeric' : undefined} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} onBlur={() => { if (v !== (value == null ? '' : String(value))) onChange(v); }} /></label>;
}

/** The confirm card: which item, the values (all can be changed), what Lazy checked, and Confirm. */
function ConfirmCard({ cmd, onChange, onDone }: { cmd: Cmd; onChange: (c: Cmd) => void; onDone: (c: Cmd) => void }) {
  const plan = cmd.plan!;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const f = plan.fields;
  const choice = plan.options.find((o) => o.id === plan.choice);
  // every change made on the card, sent again with Confirm so a tap right after typing saves what is on screen
  const edits = useRef<{ choice?: string; fields: Fields }>({ fields: {} });

  async function edit(body: { choice?: string; fields?: Fields }) {
    setErr(null);
    edits.current = { choice: body.choice ?? edits.current.choice, fields: { ...edits.current.fields, ...body.fields } };
    try { onChange(await api<Cmd>(`/api/lazy/commands/${cmd.id}/check`, { body })); } catch (e) { setErr(e); }
  }
  async function confirm() {
    setBusy(true); setErr(null);
    try { onDone(await api<Cmd>(`/api/lazy/commands/${cmd.id}/confirm`, { body: edits.current })); }
    catch (e) { if (e instanceof ApiError && e.detail?.plan) onChange({ ...cmd, plan: e.detail.plan }); setErr(e); }
    finally { setBusy(false); }
  }
  async function cancel() {
    setBusy(true);
    try { await api(`/api/lazy/commands/${cmd.id}/cancel`, { body: {} }); onDone({ ...cmd, status: 'cancelled', message: 'Cancelled. Nothing was saved.' }); }
    catch (e) { setErr(e); } finally { setBusy(false); }
  }
  const num = (v: string) => (v.trim() === '' ? null : Math.max(0, Math.round(Number(v)) || 0));

  return (
    <div className="lazy-card">
      <div className="faint">{plan.title}</div>
      <div className="lazy-question">{plan.question}</div>
      {plan.options.length > 1 && (
        <fieldset className="lazy-options">
          <legend className="faint">Which item?</legend>
          {plan.options.map((o) => (
            <label key={o.id} className={`lazy-option${o.id === plan.choice ? ' on' : ''}`}>
              <input type="radio" name={`opt-${cmd.id}`} checked={o.id === plan.choice} onChange={() => edit({ choice: o.id })} />
              <span><b>{o.label}</b>{o.detail && <span className="faint"> · {o.detail}</span>}</span>
            </label>
          ))}
        </fieldset>
      )}
      {plan.options.length === 1 && choice && <div className="faint">{choice.label}{choice.detail ? ` · ${choice.detail}` : ''}</div>}

      <div className="field-row">
        {plan.action === 'add_item' && choice?.kind === 'new' && <>
          <TextInput label="Brand" value={f.brand} onChange={(v) => edit({ fields: { brand: v } })} />
          <TextInput label="Name" value={f.name} onChange={(v) => edit({ fields: { name: v } })} />
          <TextInput label="Size" value={f.size} placeholder="750ml, 1.75L, 12oz" onChange={(v) => edit({ fields: { size: v } })} />
          <TextInput label="Units in the pack" numeric value={f.pack ?? 1} onChange={(v) => edit({ fields: { pack: Math.max(1, num(v) ?? 1) } })} />
          <label>Kind<select value={f.category ?? 'spirits'} onChange={(e) => edit({ fields: { category: e.target.value } })}>{CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}</select></label>
          <TextInput label="Barcode (blank: make one)" value={f.code ?? ''} onChange={(v) => edit({ fields: { code: v.replace(/\D/g, '') || null } })} />
          <TextInput label="On hand now" numeric value={f.openingStock ?? ''} onChange={(v) => edit({ fields: { openingStock: num(v) } })} />
        </>}
        {plan.action === 'add_item' && choice?.kind !== 'store' && <MoneyInput label="Cost each" cents={f.costCents} onChange={(c) => edit({ fields: { costCents: c } })} />}
        {(plan.action === 'add_item' || plan.action === 'change_price') && <MoneyInput label={plan.action === 'add_item' && choice?.kind !== 'store' ? 'Selling price' : 'New shelf price'} cents={f.priceCents} onChange={(c) => edit({ fields: { priceCents: c } })} />}
        {(plan.action === 'count_stock' || plan.action === 'set_reorder_point') && <TextInput label={plan.action === 'count_stock' ? 'On hand' : 'Reorder at'} numeric value={f.qty} onChange={(v) => edit({ fields: { qty: num(v) } })} />}
        {plan.action === 'set_location' && <TextInput label="Location" value={f.location} onChange={(v) => edit({ fields: { location: v } })} />}
      </div>

      {plan.facts.length > 0 && <ul className="lazy-facts">{plan.facts.map((x) => <li key={x}>{x}</li>)}</ul>}
      {plan.warnings.map((w) => <div key={w} className="notice warn">{w}</div>)}
      {plan.blocked && <div className="notice bad">{plan.blocked}</div>}
      {!plan.blocked && <ErrorNote error={err} />}
      <div className="row wrap">
        <button className="primary lazy-confirm" disabled={busy || !!plan.blocked} onClick={confirm}><Icon name="check" />{busy ? 'Saving…' : 'Confirm'}</button>
        <button disabled={busy} onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

function Result({ cmd }: { cmd: Cmd }) {
  const invoices = cmd.result?.invoices ?? [];
  return (
    <div className={`lazy-card ${cmd.status === 'done' ? 'done' : ''}`}>
      <div className="lazy-question">{cmd.message ?? cmd.result?.message}</div>
      {invoices.length > 0 && (
        <ul className="lazy-invoices">
          {invoices.map((i, n) => (
            <li key={`${i.invoiceId}-${n}`}>
              <Link to={i.link}><b>{i.date ?? 'No date'}</b> · {i.wholesaler ?? 'Distributor'} · invoice {i.invoiceNo ?? '—'}</Link>
              <div className="faint">{i.item ?? i.line}: {i.cases ? `${i.cases} cs` : ''}{i.cases && i.bottles ? ' + ' : ''}{i.bottles ? `${i.bottles} btl` : ''}{i.caseCents ? ` at ${usd(i.caseCents)}/cs` : ''}</div>
            </li>
          ))}
        </ul>
      )}
      {invoices[0] && <Link className="btn primary" to={invoices[0].link}><Icon name="doc" />Open the last invoice</Link>}
      {cmd.status === 'done' && cmd.result?.link === '/labels' && <Link className="btn" to="/labels"><Icon name="tag" />Shelf labels</Link>}
    </div>
  );
}

/** The whole Lazy AI tool: microphone, what you said, and the card. Used on its own page, in the panel and on the phone. */
export function LazyAI({ header, showHistory }: { header?: ReactNode; showHistory?: boolean }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [cmd, setCmd] = useState<Cmd | null>(null);
  const [recent, setRecent] = useState<Cmd[]>([]);
  const [aiOn, setAiOn] = useState<boolean | null>(null);
  const spoken = useRef(false);
  const voice = useVoice((t, final) => { setText(t); if (final) { spoken.current = true; void say(t); } });

  const loadRecent = () => api<{ ai: boolean; commands: Cmd[] }>('/api/lazy').then((r) => { setAiOn(r.ai); setRecent(r.commands); }).catch(() => {});
  useEffect(() => { void loadRecent(); }, []);

  async function say(t = text) {
    const said = t.trim();
    if (!said || busy) return;
    setBusy(true); setErr(null); setCmd(null);
    try {
      const r = await api<Cmd>('/api/lazy/say', { body: { text: said } });
      setCmd(r);
      if (spoken.current) speak(r.plan?.question ?? r.message ?? '');
      void loadRecent();
    } catch (e) { setErr(e); }
    finally { setBusy(false); spoken.current = false; }
  }
  function finished(c: Cmd) { setCmd(c); setText(''); void loadRecent(); }

  return (
    <div className="lazy">
      {header}
      <div className="lazy-body">
        <div className="lazy-mic-row">
          {voice.supported ? (
            <button className={`lazy-mic${voice.listening ? ' on' : ''}`} onClick={voice.listening ? voice.stop : voice.start} aria-label={voice.listening ? 'Stop listening' : 'Tap and talk'}>
              <Icon name="mic" size={30} />
            </button>
          ) : null}
          <div className="grow">
            <b>{voice.listening ? 'Listening… say it now' : voice.supported ? 'Tap the microphone and say it' : 'Type what to do'}</b>
            <div className="faint">{voice.supported ? 'Like "Hey Lazy, add Budweiser 12-pack, cost $10.99, sell it for $15.99". Nothing is saved until you tap Confirm.' : 'This browser cannot listen. Chrome, Edge and Safari on iPhone can.'}</div>
          </div>
        </div>
        <form className="ai-input lazy-input" onSubmit={(e) => { e.preventDefault(); void say(); }}>
          <textarea rows={2} value={text} maxLength={1000} placeholder={voice.listening ? 'Listening…' : 'Or type it here'} aria-label="Tell Lazy AI what to do"
            onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void say(); } }} />
          <button className="primary" disabled={busy || !text.trim()} aria-label="Go"><Icon name="send" /></button>
        </form>
        {aiOn === false && <div className="notice warn">AI is not switched on for this POS, so Lazy only understands plain commands like the examples below.</div>}
        <ErrorNote error={err} />
        {busy && <div className="ai-msg assistant ai-typing" aria-label="Lazy AI is working"><span /><span /><span /></div>}
        {cmd?.status === 'pending' && cmd.plan && <ConfirmCard key={cmd.id} cmd={cmd} onChange={setCmd} onDone={finished} />}
        {cmd && cmd.status !== 'pending' && <Result cmd={cmd} />}
        {!cmd && !busy && (
          <div className="ai-suggest">{EXAMPLES.map((s) => <button key={s} onClick={() => { setText(s); void say(s); }}>{s}</button>)}</div>
        )}
        {showHistory && recent.length > 0 && (
          <div className="lazy-history">
            <h3>Recent commands</h3>
            <table><tbody>
              {recent.map((r) => (
                <tr key={r.id}>
                  <td className="faint">{r.createdAt ? new Date(r.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</td>
                  <td>{r.who}</td>
                  <td>“{r.said}”</td>
                  <td><span className={`chip ${r.status === 'done' ? 'good' : r.status === 'pending' ? 'warn' : ''}`}>{r.status === 'done' ? 'saved' : r.status === 'pending' ? 'not confirmed' : r.status}</span></td>
                  <td className="faint">{r.result?.message ?? ''}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}
      </div>
    </div>
  );
}

/** The Lazy AI button and its panel, beside the Shailesh AI button. */
export function LazyButton() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <>
      <button className="ai-fab lazy-fab" onClick={() => setOpen((o) => !o)} aria-expanded={open} title="Lazy AI: say a change instead of typing it">
        <Icon name="mic" size={20} /><span>Lazy AI</span>
      </button>
      {open && (
        <div className="ai-panel lazy-panel" role="dialog" aria-label="Lazy AI">
          <LazyAI header={<header className="ai-head"><Icon name="mic" /><b className="grow">Lazy AI</b><button className="ghost" onClick={() => setOpen(false)} aria-label="Close"><Icon name="x" size={16} /></button></header>} />
        </div>
      )}
    </>
  );
}
